import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { DeleteOutlineRounded as DeleteOutlineRoundedIcon } from "./icons/FigmaControlIcons";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import TabSectionHeader from "./TabSectionHeader";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";

const formatFileSize = (size) => {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return "PDF";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getDocumentName = (file = {}, index = 0) =>
  String(file.name || file.file_name || file.label || `Document ${index + 1}.pdf`).trim();

export default function ContactDocuments({
  files = [],
  uploading = false,
  deleting = false,
  error = "",
  selectedIds = [],
  canUpload = false,
  onUpload,
  onDelete,
  onSelectedIdsChange,
}) {
  const fileInputRef = useRef(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const selectedIdSet = new Set((Array.isArray(selectedIds) ? selectedIds : []).map(String));
  const documents = Array.isArray(files) ? files : [];

  const toggleSelected = (fileId) => {
    const normalizedId = String(fileId || "");
    if (!normalizedId) return;
    const next = selectedIdSet.has(normalizedId)
      ? [...selectedIdSet].filter((id) => id !== normalizedId)
      : [...selectedIdSet, normalizedId];
    onSelectedIdsChange?.(next);
  };

  const handleFileChange = (event) => {
    onUpload?.(event);
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={handleFileChange}
      />

      <TabSectionHeader
        title="Documents"
        dividerColor="#8ED0FF"
        actionNode={
          <Stack direction="row" spacing={1} alignItems="center">
            {selectedIdSet.size ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                disabled={deleting}
                onClick={onDelete}
                sx={{
                  borderRadius: "999px",
                  color: "#B42318",
                  borderColor: "#FDA29B",
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                {deleting ? "Deleting..." : `Delete (${selectedIdSet.size})`}
              </Button>
            ) : null}
            <Button
              size="small"
              variant="contained"
              className={withPillButtonClassName()}
              startIcon={
                uploading ? <CircularProgress size={14} color="inherit" /> : <AddRoundedIcon sx={{ fontSize: 16 }} />
              }
              disabled={!canUpload || uploading}
              onClick={() => fileInputRef.current?.click()}
              sx={[
                pillButtonInteractiveSx,
                pillButtonFilledSx,
                {
                  minWidth: 170,
                  color: "#fff",
                  fontWeight: 400,
                  boxShadow: "none",
                  "&.Mui-disabled": { color: "#fff", opacity: 0.5 },
                },
              ]}
            >
              {uploading ? "Uploading..." : "Add New Document"}
            </Button>
          </Stack>
        }
      />

      {error ? (
        <Typography sx={{ color: "#DC2626", fontSize: 12, fontWeight: 600, mt: 1.5 }}>
          {error}
        </Typography>
      ) : null}

      {!documents.length && !error ? (
        <Box
          sx={{
            mt: 3,
            border: "1px dashed #D7E0EA",
            borderRadius: 2.5,
            backgroundColor: "#F8FAFC",
            px: { xs: 2, md: 2.5 },
            py: { xs: 2.5, md: 3 },
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.75,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054" }}>
              No documents yet
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 12.5, color: "#667085", lineHeight: 1.55 }}>
              Upload PDF files here to keep contact reference material aligned with the other detail sections.
            </Typography>
          </Box>
          {canUpload ? (
            <Button
              size="small"
              variant="contained"
              className={withPillButtonClassName()}
              startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              sx={[pillButtonInteractiveSx, pillButtonFilledSx, { px: 1.8, fontWeight: 400, boxShadow: "none" }]}
            >
              Add New Document
            </Button>
          ) : null}
        </Box>
      ) : null}

      {documents.length ? (
        <Box sx={{ mt: 3, display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
          {documents.map((file, index) => {
            const fileId = String(file?.id || `${file?.activity_id || "file"}-${file?.attachment_index ?? index}`);
            const checked = selectedIdSet.has(fileId);
            const name = getDocumentName(file, index);
            const url = String(file?.url || "").trim();

            return (
              <Box
                key={fileId}
                onClick={() => url && setPreviewDocument({ name, url })}
                sx={{
                  borderRadius: 2.5,
                  border: "1px solid #D7E0EA",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 2,
                  py: 1.75,
                  cursor: url ? "pointer" : "default",
                  transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
                  "&:hover": url ? { borderColor: "#B8CCFF", boxShadow: "0 12px 24px rgba(52,81,244,0.08)", transform: "translateY(-1px)" } : undefined,
                }}
              >
                <Checkbox
                  checked={checked}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => toggleSelected(fileId)}
                  sx={{ p: 0.25, mt: 0.25, color: "#D0D5DD", "&.Mui-checked": { color: "#9C4FFF" } }}
                />
                <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, backgroundColor: "#F5F8FF", border: "1px solid #D9E5F3", display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: "#3451F4" }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 600, wordBreak: "break-word" }}>
                      {name}
                    </Typography>
                    <Typography sx={{ color: "#667085", fontSize: 12, mt: 0.45, wordBreak: "break-word" }}>
                      {formatFileSize(file?.file_size || file?.size)}
                    </Typography>
                  </Box>
                </Box>
                <Stack alignItems="flex-end" spacing={1} sx={{ flex: "0 0 auto", pl: 1 }}>
                  <Box sx={{ px: 1, py: 0.45, borderRadius: 999, backgroundColor: "#ECFDF3", color: "#027A48", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
                    Uploaded
                  </Box>
                  {url ? <OpenInNewRoundedIcon sx={{ fontSize: 18, color: "#9C4FFF" }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#9C4FFF" }} />}
                </Stack>
              </Box>
            );
          })}
        </Box>
      ) : null}

      <Dialog
        open={Boolean(previewDocument)}
        onClose={() => setPreviewDocument(null)}
        maxWidth={false}
        PaperProps={{ sx: { width: { xs: "calc(100% - 24px)", md: "min(1100px, calc(100% - 80px))" }, height: { xs: "calc(100% - 24px)", md: "calc(100% - 64px)" }, borderRadius: 3, overflow: "hidden" } }}
      >
        <Box sx={{ px: 2.25, py: 1.5, borderBottom: "1px solid #EAECF0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography data-popup-section sx={{ fontWeight: 700, color: "#3451F4", fontSize: 15 }}>Document</Typography>
            <Typography sx={{ fontSize: 12, color: "#667085" }} noWrap>{previewDocument?.name || "PDF preview"}</Typography>
          </Box>
          <Stack data-popup-header direction="row" alignItems="center" spacing={1}>
            <Button component="a" href={previewDocument?.url || "#"} target="_blank" rel="noopener noreferrer" size="small" startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: "none", color: "#9C4FFF", fontWeight: 500 }}>
              Open in new tab
            </Button>
            <IconButton data-popup-close onClick={() => setPreviewDocument(null)} size="small"><CloseRoundedIcon sx={{ fontSize: 18, color: "#475467" }} /></IconButton>
          </Stack>
        </Box>
        <Box sx={{ flex: 1, height: "100%", p: { xs: 1.5, md: 2 }, background: "#fff" }}>
          <iframe title={previewDocument?.name || "PDF preview"} src={previewDocument?.url ? `${previewDocument.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH` : "about:blank"} style={{ width: "100%", height: "calc(100vh - 180px)", minHeight: "620px", border: 0, display: "block" }} />
        </Box>
      </Dialog>
    </Box>
  );
}
